import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TealiumAnalyticsService, TealiumConstants, TrackEventUtil } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { Model } from '@ddt-web/data-service';
import { DialogRef } from '@ddt-web/dialog';
import { ModelPortfolio } from '@ddt-web/domain';
import { Memoize, ModelNameUtil } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { UrlRedirectService, UrlService } from '@ddt-web/url';
import * as get from 'lodash/get';

@Component({
	selector: 'mp-model-list-table',
	templateUrl: './model-list-table.component.html',
	styleUrls: ['./model-list-table.component.scss']
})
export class ModelListTableComponent {
	@Input() public models: Array<any> = [];
	@Input() public tableMode: string = '';
	@Input() public showCheckboxes: boolean = false;
	@Input() public allowMultiple: boolean = true;
	@Input() public analyticsModelType: string;
	@Input() public selectedModelPortfolios: ModelPortfolio[] = [];
	@Input() public maxLimit: number = 100;
	@Input() public useDasPortfolioId: boolean = false;
	@Output() public closeEnclosingModal = new EventEmitter();
	@Output() public onModelSelected: EventEmitter<any> = new EventEmitter();

	/**
	 * Note: 
	 * this.selectedModelPortfolios is being used in ST
	 * this.selectedModelIdsFromUrl is being used in PA
	 * We should merge this functionality in the future
	 */
	public selectedModelIdsFromUrl: Array<string> = [];

	constructor(
		public dialogRef: DialogRef,
		private urlService: UrlService,
		private urlRedirectService: UrlRedirectService,
		private applicationStateProxyService: ApplicationStateStoreProxyService,
		public tealiumAnalyticsService: TealiumAnalyticsService
	) {
		this.selectedModelIdsFromUrl = this.urlService.getPortfolioIds() as Array<string> || [];
	}

	public closeModal(id?: string): void {
		this.closeEnclosingModal.emit(true);
		if (id) {
			// reset custom advisory fee if new analysis
			this.applicationStateProxyService.updateCustomAdvisoryFee({ customAdvisoryFee: null } as PerformanceState);

			this.trackPortfolioChangeEvent(id, TealiumConstants.LINK_NAMES.VIEW_360_ANALYSIS_EVENT);
			this.urlRedirectService.redirectToTabByQueryParam([id], ApplicationPathConstants.PORTFOLIO_ANALYSIS_PATHS, false);
		}
	}

	public addToComparison(model: any): void {
		if (!this.existsInSelectedModelIdsFromUrl(model)) {
			this.closeModal();
			this.selectedModelIdsFromUrl.push(get(model, 'id', ''));
			const ids = this.selectedModelIdsFromUrl.join(',');
			this.trackPortfolioChangeEvent(ids, TealiumConstants.LINK_NAMES.ADD_TO_COMPARE_EVENT);
			this.tealiumAnalyticsService.triggerToolCompletion(ids, null, TealiumConstants.LINK_NAMES.ADD_TO_COMPARE_EVENT);
			this.urlRedirectService.redirectToOverviewTab(this.selectedModelIdsFromUrl, false, true);
		}
	}

	public chooseReplacement(selectedModel: any): void {
		this.dialogRef.close(selectedModel);
	}

	public isModelSelected(model: any): boolean {
		return this.selectedModelPortfolios.some(selectedModel => get(selectedModel, 'id', '') === get(model, this.useDasPortfolioId ? 'dasPortfolioId' : 'id'));
	}

	public isView360AnalysisDisabled(model: any): boolean {
		// We should disable View 360 Analysis for the specific model if only that is selected
		if (get(this.selectedModelIdsFromUrl, 'length', 0) === 1) {
			return this.selectedModelIdsFromUrl[0] === get(model, 'id');
		} else {
			return false;
		}
	}

	public existsInSelectedModelIdsFromUrl(model: any): boolean {
		return this.selectedModelIdsFromUrl.some(selectedModelId => selectedModelId === get(model, 'id'));
	}

	public onModelSelectionClick(event: any, model: any): void {
		if (this.allowMultiple) {
			const checked = event.source?.checked;
			const index = this.selectedModelPortfolios.findIndex(selectedModel => get(selectedModel, 'id', '') === get(model, 'id'));
			if (checked && index === -1) {
				// If model doesn't exist, add it to 'selected'
				this.addToSelectedModels(model);
			} else {
				// If model exists, remove it from 'selected'
				if (index !== -1) {
					this.deleteFromSelectedModels(index, 1);
				}
			}
		} else {
			this.deleteFromSelectedModels(0, this.selectedModelPortfolios.length);
			this.addToSelectedModels(model);
		}
		this.onModelSelected.emit();
	}

	private deleteFromSelectedModels(index: number, deleteCount: number): void {
		this.selectedModelPortfolios.splice(index, deleteCount);
	}

	private addToSelectedModels(model: any): void {
		this.selectedModelPortfolios.push(model);
	}

	private trackPortfolioChangeEvent(ids: string, linkName: string): void {
		// Selection Model
		const analyticsName = this.analyticsModelType ? `${linkName}:${this.analyticsModelType}` : linkName;
		const event = TrackEventUtil.createPortfolioChangeEvent(analyticsName, ids, TealiumConstants.EVENT_PROPERTY_NAMES.PORTFOLIO_ADDED);
		this.tealiumAnalyticsService.trackEventTealium(event);
	}

	public isRowDisabled(model: any): boolean {
		return !this.isModelSelected(model) && this.selectedModelPortfolios.length >= this.maxLimit;
	}

	@Memoize()
	public getModelName(model: Model): string {
		return ModelNameUtil.getModelExternalName(model, model?.name);
	}
}
